import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext.jsx';
import SignIn from './pages/SignIn.jsx';
import NoAccess from './pages/NoAccess.jsx';
import Plan from './pages/Plan.jsx';
import Blogs from './pages/Blogs.jsx';
import BlogReader from './pages/BlogReader.jsx';
import BlogReview from './pages/BlogReview.jsx';
import Questions from './pages/Questions.jsx';
import Attention from './pages/Attention.jsx';
import Strategy from './pages/Strategy.jsx';
import RequestReset from './pages/RequestReset.jsx';
import SetPassword from './pages/SetPassword.jsx';
import AdminClientsNew from './pages/AdminClientsNew.jsx';
import AdminOnboarding from './pages/AdminOnboarding.jsx';
import AdminAnalysis from './pages/AdminAnalysis.jsx';
import AdminPlan from './pages/AdminPlan.jsx';
import AdminClientSettings from './pages/AdminClientSettings.jsx';
import Results from './pages/Results.jsx';
import Placeholder from './pages/Placeholder.jsx';
import GmbAudit from './pages/GmbAudit.jsx';
import AdminGmb from './pages/AdminGmb.jsx';
import AdminAdhoc from './pages/AdminAdhoc.jsx';
import SeoRecommendations from './pages/SeoRecommendations.jsx';
import Course from './pages/Course.jsx';
import CourseLesson from './pages/CourseLesson.jsx';
import CoursePrompts from './pages/CoursePrompts.jsx';
import AdminCourses from './pages/AdminCourses.jsx';
import AdminSeoTasks from './pages/AdminSeoTasks.jsx';
// Session 33: the sign-up page is public, and what it produces is agency-only.
import Signup from './pages/Signup.jsx';
// Session 49 (Wave 14): the newsletter's own page, and Learn more for a locked service
import Newsletters from './pages/Newsletters.jsx';
import Learn from './pages/Learn.jsx';
import Welcome from './pages/Welcome.jsx';
import AdminSignups from './pages/AdminSignups.jsx';
// Session 55 (Wave 16): every client on one page, agency only
import AdminClients from './pages/AdminClients.jsx';
import LeftRail from './components/LeftRail.jsx';
import HelpWidget from './components/HelpWidget.jsx';   // Session 67: the help assistant

// Session 33: /signup belongs here because a stranger has no session. It
// reads the price list with the anon key and writes one row through
// portal_request_signup, which validates everything it is handed.
const PUBLIC_AUTH_PATHS = new Set(['/reset', '/set-password', '/signup']);

// Session 55: an agency user lands on the Clients page once per browser tab
// (the first time "/" is asked for after sign-in), then "/" is the inbox for
// the active client as before. sessionStorage so a new tab lands there again
// and a refresh does not. Wrapped because storage can throw in private windows.
function AgencyHome() {
  const navigate = useNavigate();
  const [decided, setDecided] = useState(false);
  useEffect(() => {
    let first = false;
    try {
      if (!sessionStorage.getItem('dl-landed')) { sessionStorage.setItem('dl-landed', '1'); first = true; }
    } catch { first = false; }
    if (first) navigate('/admin/clients', { replace: true });
    else setDecided(true);
  }, [navigate]);
  return decided ? <Attention /> : null;
}

export default function App() {
  const { session, me, loading, error, activeClient, isAgency } = useAuth();
  const location = useLocation();
  const isPublicAuth = PUBLIC_AUTH_PATHS.has(location.pathname);

  if (isPublicAuth) {
    return (
      <Routes>
        <Route path="/reset"        element={<RequestReset />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/signup"       element={<Signup />} />
      </Routes>
    );
  }

  if (loading) return <FullScreenNote>Loading…</FullScreenNote>;
  if (!session) return <SignIn />;
  if (!me) return <NoAccess />;
  if (error) return <FullScreenNote>Something went wrong: {error}</FullScreenNote>;

  return (
    <div className="app-shell">
      <LeftRail />
      <HelpWidget />
      <main className="wrap">
        <Routes>
          {/* Session 36: a provisional client lands on their setup rather
              than on an empty dashboard. Leena asked for exactly this: "can we
              not automatically redirect them to a login page where we have
              created what they are able to see". Only while their status is
              'onboarding' — she decides when they become active, and from then
              on "/" is the dashboard like anyone else's. */}
          <Route path="/"                       element={
            activeClient?.status === 'onboarding' && !isAgency
              ? <Navigate to="/welcome" replace />
              : isAgency
                ? <AgencyHome />
                : <Attention />
          } />
          <Route path="/welcome"                element={<Welcome />} />
          <Route path="/plan"                   element={<Plan />} />
          <Route path="/blogs"                  element={<Blogs />} />
          <Route path="/blogs/:slug"            element={<BlogReader />} />
          <Route path="/blogs/:slug/review"     element={<BlogReview />} />
          <Route path="/blogs/:slug/questions"  element={<Questions />} />
          <Route path="/strategy"               element={<Strategy />} />
          {/* Session 49: Newsletters ready, and Learn more for anything locked */}
          <Route path="/newsletters"            element={<Newsletters />} />
          <Route path="/learn/:group"           element={<Learn />} />
          <Route path="/results"                element={<Results />} />
          {/* Session 27: AI courses. /prompts before /:courseKey so the
              literal path wins over the parameter. */}
          <Route path="/courses/prompts"                 element={<CoursePrompts />} />
          <Route path="/courses/:courseKey"              element={<Course />} />
          <Route path="/courses/:courseKey/:lessonKey"   element={<CourseLesson />} />
          {/* Session 10: GMB Audit (released audit, else the placeholder) */}
          <Route path="/gmb-audit"                       element={<GmbAudit />} />
          {/* Session 22: SEO recommendations (released checklist, else the placeholder) */}
          <Route path="/seo-recommendations"             element={<SeoRecommendations />} />
          {/* Session 55: the overview; /new keeps its place below */}
          <Route path="/admin/clients"          element={<AdminClients />} />
          <Route path="/admin/clients/new"      element={<AdminClientsNew />} />
          <Route path="/admin/clients/:clientId/onboarding" element={<AdminOnboarding />} />
          <Route path="/admin/clients/:clientId/analysis"   element={<AdminAnalysis />} />
          <Route path="/admin/clients/:clientId/settings"   element={<AdminClientSettings />} />
          <Route path="/admin/clients/:clientId/gmb"        element={<AdminGmb />} />
          <Route path="/admin/clients/:clientId/seo-tasks"  element={<AdminSeoTasks />} />
          {/* Session 16: ad hoc blogs (agency only) */}
          <Route path="/admin/clients/:clientId/adhoc"        element={<AdminAdhoc />} />
          <Route path="/admin/clients/:clientId/adhoc/:jobId" element={<AdminAdhoc />} />
          <Route path="/admin/plans/:jobId"                 element={<AdminPlan />} />
          {/* Session 27: the agency's course view */}
          <Route path="/admin/courses"                      element={<AdminCourses />} />
          {/* Session 33: what came in from the sign-up page */}
          <Route path="/admin/signups"                      element={<AdminSignups />} />
          <Route path="*"                       element={<Navigate to="/" replace state={{ from: location }} />} />
        </Routes>
      </main>
    </div>
  );
}

function FullScreenNote({ children }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: 'var(--dl-body)', fontSize: 15,
    }}>
      {children}
    </div>
  );
}
